import React, { FC, useState, useCallback, useMemo } from 'react';
import { observer } from 'mobx-react';
import {
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import { ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import styled from 'styled-components';
import Dialog from 'components/UI/Dialog';
import Button from 'components/UI/Button';
import Input from 'components/UI/Input';
import Grid from 'components/UI/Grid';
import Text from 'components/UI/Text';
import { theme } from 'components/UI';
import { useTranslation } from 'react-i18next';
import Space from 'components/UI/Space';
import { DialogOnCloseReason } from 'models/shared';
import api from 'Api/ApiMethods';

const MB_COUNT = 1;
// Validate file size (e.g., max 1MB)
const MAX_SIZE = MB_COUNT * 1024 * 1024; // 1MB

const NoWrappingText = styled(Text)`
  white-space: nowrap;
  min-width: 30%;
  max-width: 30%;
`;

const FileInputWrapper = styled.div`
  width: 100%;
`;

const StyledFileInput = styled.input`
  width: 100%;
  padding: 8px;
  border: 1px solid var(--surface-border);
  border-radius: 4px;
  background-color: var(--surface-ground);
  color: var(--text-color);
  cursor: pointer;

  &::-webkit-file-upload-button {
    padding: 6px 12px;
    border: 1px solid var(--surface-border);
    border-radius: 4px;
    background-color: var(--surface-100);
    color: var(--text-color);
    cursor: pointer;
    margin-right: 10px;

    &:hover {
      background-color: var(--surface-200);
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ImagePreview = styled.img`
  max-width: 100%;
  max-height: 200px;
  border-radius: 4px;
  border: 1px solid var(--surface-border);
  margin-top: 10px;
`;

const UploadStatus = styled(Text) <{
  status: 'idle' | 'uploading' | 'success' | 'error';
}>`
  margin-top: 10px;
  color: ${({ status }) => {
    switch (status) {
      case 'uploading':
        return 'var(--blue-500)';
      case 'success':
        return 'var(--green-500)';
      case 'error':
        return 'var(--red-500)';
      default:
        return 'var(--text-color-secondary)';
    }
  }};
`;

interface CampaignUploadS3DialogProps {
  onHide: () => void;
  visible: boolean;
  title: string;
  onUploadSuccess?: (url: string) => void;
}

const CampaignUploadS3Dialog: FC<CampaignUploadS3DialogProps> = observer(
  ({ visible = false, onHide, title, onUploadSuccess }) => {
    const { t } = useTranslation('notifications');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [uploadedUrl, setUploadedUrl] = useState<string>('');
    const [uploadStatus, setUploadStatus] = useState<
      'idle' | 'uploading' | 'success' | 'error'
    >('idle');

    const [errorMessage, setErrorMessage] = useState<string>('');
    const [copied, setCopied] = useState<boolean>(false);

    const isValidForm = useMemo(() => {
      return selectedFile !== null && uploadStatus !== 'uploading';
    }, [selectedFile, uploadStatus]);

    const closeDialog = useCallback(() => {
      setSelectedFile(null);
      setPreviewUrl(null);
      setUploadedUrl('');
      setUploadStatus('idle');
      setErrorMessage('');
      onHide();
    }, [onHide]);

    const handleCloseDialog = useCallback(
      (event: unknown, reason: DialogOnCloseReason) => {
        if (reason && reason === DialogOnCloseReason.BackDropClick) {
          return;
        }

        closeDialog();
      },
      [closeDialog],
    );

    const handleCloseButton = useCallback(() => {
      closeDialog();
    }, [closeDialog]);

    const handleFileChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];

        if (file) {
          // Validate file type
          if (!file.type.startsWith('image/')) {
            setErrorMessage(t('invalid_image_file'));
            setUploadStatus('error');

            return;
          }

          if (file.size > MAX_SIZE) {
            setErrorMessage(t('file_size_limit', { maxSize: MB_COUNT }));
            setUploadStatus('error');

            return;
          }

          setSelectedFile(file);
          setUploadStatus('idle');
          setErrorMessage('');

          // Create preview URL
          const reader = new FileReader();

          reader.onloadend = () => {
            setPreviewUrl(reader.result as string);
          };

          reader.readAsDataURL(file);
        }
      },
      [t],
    );

    const handleUploadClick = useCallback(async () => {
      if (!selectedFile) return;

      setUploadStatus('uploading');
      setErrorMessage('');

      try {
        const formData = new FormData();

        formData.append('file', selectedFile);
        formData.append('fileName', selectedFile.name);

        const response = await api.postCampaignS3Image(formData);
        const url =
          response.data?.url || response.url || response?.data || response;

        setUploadedUrl(url);
        setUploadStatus('success');

        if (onUploadSuccess) {
          onUploadSuccess(url);
        }
      } catch (error) {
        // Upload failed
        setUploadStatus('error');
        setErrorMessage(t('upload_failed_try_again'));
      }
    }, [selectedFile, onUploadSuccess, t]);

    const getStatusMessage = () => {
      switch (uploadStatus) {
        case 'uploading':
          return t('uploading');
        case 'success':
          return t('upload_successful', { url: uploadedUrl });
        case 'error':
          return errorMessage || t('upload_failed');
        default:
          return '';
      }
    };

    const handleCopyUrl = useCallback(async () => {
      try {
        await navigator.clipboard.writeText(uploadedUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (error) {
        // Fallback for older browsers or if clipboard API fails
        const textArea = document.createElement('textarea');
        textArea.value = uploadedUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }, [uploadedUrl]);

    return (
      <Dialog
        open={visible}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
        dialogTitle={title}
        titleWeight="bold"
        disableEscapeKeyDown
      >
        <DialogContent>
          <Space height={1} />
          <Grid direction="column" gap="10px">
            <Grid direction="row" gap="10px" alignSelf="start" grow>
              <NoWrappingText>{t('image_file')}:</NoWrappingText>
              <FileInputWrapper>
                <StyledFileInput
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  disabled={uploadStatus === 'uploading'}
                />
              </FileInputWrapper>
            </Grid>

            {selectedFile && (
              <Grid direction="row" gap="10px" alignSelf="start" grow>
                <NoWrappingText>{t('file_name')}:</NoWrappingText>
                <Text>{selectedFile.name}</Text>
              </Grid>
            )}

            {selectedFile && (
              <Grid direction="row" gap="10px" alignSelf="start" grow>
                <NoWrappingText>{t('file_size')}:</NoWrappingText>
                <Text>{(selectedFile.size / 1024).toFixed(2)} KB</Text>
              </Grid>
            )}

            {previewUrl && (
              <Grid direction="column" gap="10px" alignSelf="center">
                <Text weight="bold">{t('preview')}:</Text>
                <ImagePreview src={previewUrl} alt="Preview" />
              </Grid>
            )}

            {uploadStatus !== 'idle' && (
              <Grid direction="row" gap="10px" alignSelf="start" grow>
                <UploadStatus status={uploadStatus}>
                  {getStatusMessage()}
                </UploadStatus>
              </Grid>
            )}

            {uploadedUrl && uploadStatus === 'success' && (
              <Grid direction="row" gap="10px" alignSelf="start" grow>
                <NoWrappingText>{t('url')}:</NoWrappingText>
                <Grid direction="row" gap="5px" grow>
                  <Input
                    value={uploadedUrl}
                    variant="outlined"
                    type="text"
                    fullWidth
                    disabled
                  />
                  <Tooltip title={copied ? t('copied') : t('copy_url')}>
                    <IconButton
                      onClick={handleCopyUrl}
                      color={copied ? 'success' : 'primary'}
                      size="small"
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </Grid>
            )}
          </Grid>
          <Space height={1} />
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button
            onClick={handleUploadClick}
            disabled={!isValidForm || uploadStatus === 'success'}
          >
            <Text textcolor={theme.color.white} size="sm">
              {uploadStatus === 'uploading' ? t('uploading') : t('upload')}
            </Text>
          </Button>
          <Button onClick={handleCloseButton}>
            <Text textcolor={theme.color.white} size="sm">
              {uploadStatus === 'success' ? t('close') : t('cancel')}
            </Text>
          </Button>
        </DialogActions>
      </Dialog>
    );
  },
);

export default CampaignUploadS3Dialog;
